/**
 * Community Guidelines for Attractr + Audience Lab — June 2026 Legal
 * Framework supplied by the client (Akuntsu Inc.).
 *
 * EN is the canonical text. FR is being prepared by the client; until
 * it lands, the FR locale shows a short notice and renders the
 * authoritative EN copy so users still see the current guidelines.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink } from '../../components';
import classNames from 'classnames';

import css from './CommunityGuidelines.module.css';
import { useIntl } from '../../util/reactIntl';

const EnglishContent = () => (
  <>
    <p className={css.lastUpdated}>Effective Date: June 22, 2026</p>

    <h2>Our Mission</h2>
    <p>
      Attractr and Audience Lab exist to build the digital infrastructure for the performing arts —
      transforming the creative process into a participatory economy where cultural resonance drives
      global impact. We provide tools and spaces for creators to share behind-the-scenes content,
      validate artistic concepts with audiences, and learn production strategies from their peers.
    </p>

    <h2>Community Values</h2>
    <ul>
      <li>
        <strong>Asset Sovereignty:</strong> We respect creators' intellectual property rights and
        the moral rights associated with their artistic work. Do not share or reproduce others'
        content without permission.
      </li>
      <li>
        <strong>Authentic Engagement:</strong> We prioritize genuine human connections and
        process-sharing over impersonal marketing.
      </li>
      <li>
        <strong>Data Integrity:</strong> We are committed to using AI and data ethically to support
        human creativity. Do not attempt to manipulate metrics or game the system.
      </li>
      <li>
        <strong>Diversity and Inclusion:</strong> We celebrate the diverse backgrounds, experiences,
        and perspectives within the global performing arts community.
      </li>
    </ul>

    <h2>Encouraged Behaviors</h2>
    <ul>
      <li>
        <strong>Share your creative process:</strong> Post behind-the-scenes content like rehearsal
        clips, set designs, and works-in-progress.
      </li>
      <li>
        <strong>Collaborate with peers:</strong> Exchange ideas, experiences, and best practices
        with fellow creators in Audience Lab.
      </li>
      <li>
        <strong>Validate artistic concepts:</strong> Use Attractr's tools to test creative ideas and
        gather audience insights.
      </li>
      <li>
        <strong>Respect intellectual property:</strong> When sharing others' content, give proper
        attribution and obtain necessary permissions.
      </li>
      <li>
        <strong>Keep it professional:</strong> Maintain a respectful, inclusive tone in your
        interactions.
      </li>
    </ul>

    <h2>Prohibited Content and Conduct</h2>
    <ul>
      <li>
        Harassment, hate speech, and discrimination based on race, ethnicity, national origin,
        religion, sex, gender, sexual orientation, age, disability, or other protected
        characteristics.
      </li>
      <li>
        Graphic violence and sexually explicit material. Artistic nudity is permitted if
        appropriately labeled and contextualized.
      </li>
      <li>
        <strong>Harm to minors:</strong> Do not share content that exploits or endangers minors.
      </li>
      <li>
        <strong>Misinformation and disinformation:</strong> Do not spread false or misleading
        information.
      </li>
      <li>
        <strong>Spam and unauthorized solicitation:</strong> Do not repeatedly post promotional
        content unrelated to the performing arts.
      </li>
      <li>
        <strong>Copyright and trademark infringement:</strong> Respect others' intellectual
        property.
      </li>
      <li>
        <strong>Doxing and invasion of privacy:</strong> Do not share others' personal information
        without their explicit consent.
      </li>
      <li>
        <strong>Fraud and impersonation:</strong> Do not engage in fraudulent behavior or
        impersonate other individuals or organizations.
      </li>
      <li>
        <strong>Hacking and cybercrime:</strong> Do not attempt to compromise the security or
        functionality of the platform.
      </li>
    </ul>

    <h2>Data and AI Conduct</h2>
    <p>
      The following specific conduct rules apply to how members interact with platform data and AI
      features:
    </p>
    <ul>
      <li>
        Do not attempt to manipulate, flood, or game the Clara AI cultural intelligence signals
        through artificial engagement, bots, or coordinated inauthentic behavior.
      </li>
      <li>
        Do not attempt to extract, scrape, or reverse-engineer platform data, member profiles, or
        AI-generated outputs for use outside the platform.
      </li>
      <li>
        Your participation in AI data training is strictly opt-in and voluntary. Do not represent or
        suggest to other members that their data is used for AI training without their explicit
        consent.
      </li>
    </ul>

    <h2>Reporting Violations</h2>
    <p>
      If you encounter content or behavior that violates these Community Guidelines, please report
      it to our moderation team. You can report posts directly on the platform, or contact us at{' '}
      <ExternalLink href="mailto:moderation@attractr.live">moderation@attractr.live</ExternalLink>{' '}
      with a link to the offending content, the username, and a brief description of the violation.
    </p>

    <h2>Moderation and Enforcement</h2>
    <p>
      Attractr and Audience Lab use a combination of automated filters, community reporting, and
      human review to identify and address content that violates our Guidelines. Akuntsu reserves
      sole discretion in interpreting and enforcing these Guidelines.
    </p>
    <p>Possible enforcement actions include:</p>
    <ul>
      <li>Removal of content</li>
      <li>Warning the user</li>
      <li>Temporarily suspending the user's account and privileges</li>
      <li>Permanently banning the user from the platforms</li>
      <li>Reporting illegal activities to relevant authorities</li>
    </ul>
    <p>
      If you believe your content was removed in error or your account was suspended unjustly, you
      may appeal by contacting{' '}
      <ExternalLink href="mailto:appeals@attractr.live">appeals@attractr.live</ExternalLink>. Our
      team will respond within 14 business days.
    </p>
  </>
);

const FrenchPendingBanner = () => (
  <div className={css.translationNotice}>
    <p>
      <strong>Mise à jour en cours.</strong> La version française des présentes Lignes directrices
      de la communauté est en cours de traduction. La version anglaise ci-dessous est la version en
      vigueur jusqu'à publication de la traduction française officielle.
    </p>
  </div>
);

const CommunityGuidelines = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);
  const isFrench = useIntl().locale === 'fr';

  return (
    <div className={classes}>
      {isFrench ? <FrenchPendingBanner /> : null}
      <EnglishContent />
    </div>
  );
};

CommunityGuidelines.defaultProps = {
  rootClassName: null,
  className: null,
};

CommunityGuidelines.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

export default CommunityGuidelines;
